import { supabaseBrowserClient } from './supabase/client';

// Shared browser client with JSONB helpers layered on top
export const supabase = supabaseBrowserClient;

// Types for character generation with JSONB
export interface CharacterGeneration {
  id: string;
  user_id: string;
  status: 'PENDING' | 'PROCESSING' | 'DONE' | 'ERROR';
  images: any; // JSONB field - will be array when parsed
  result_url?: string;
  dir_path?: string;
  error_message?: string;
  runpod_job_id?: string;
  created_at: string;
  updated_at: string;
}

// Helper function using JSONB for images field
export const createCharacterGeneration = async (userId: string, images: string[]): Promise<string> => {
  console.log('[Supabase-JSONB] Creating generation with images:', images);
  console.log('[Supabase-JSONB] User ID:', userId);
  
  try {
    // First try using the secure function that bypasses RLS
    try {
      const { data: funcData, error: funcError } = await (supabase as any).rpc(
        'create_character_generation_secure',
        {
          p_user_id: userId,
          p_images: JSON.stringify(images)
        }
      );
      
      if (!funcError && funcData) {
        console.log('[Supabase-JSONB] Created via secure function:', funcData);
        return funcData;
      }
      
      console.log('[Supabase-JSONB] Secure function failed:', funcError);
    } catch (e) {
      console.log('[Supabase-JSONB] Secure function not available:', e);
    }
    
    // Method 2: Direct insert with JSONB
    const { data, error } = await (supabase as any)
      .from('character_generations')
      .insert({
        user_id: userId,
        status: 'PENDING',
        images: JSON.stringify(images), // Send as JSON string for JSONB column
      })
      .select('id')
      .single();

    if (error) {
      console.error('[Supabase-JSONB] Insert error:', error);
      
      // Fallback: Try with empty array if images might be the issue
      if (error.message?.includes('URL')) {
        console.log('[Supabase-JSONB] Retrying with empty images array');
        const { data: retryData, error: retryError } = await (supabase as any)
          .from('character_generations')
          .insert({
            user_id: userId,
            status: 'PENDING',
            images: JSON.stringify([]), // Empty array
          })
          .select('id')
          .single();
          
        if (!retryError && retryData) {
          // Update with actual images after creation
          await (supabase as any)
            .from('character_generations')
            .update({ 
              images: JSON.stringify(images.map(img => 
                img.startsWith('blob:') ? 'pending_upload' : img
              ))
            })
            .eq('id', retryData.id);
            
          return retryData.id;
        }
        
        throw retryError || new Error('Failed to create generation');
      }
      
      throw error;
    }
    
    console.log('[Supabase-JSONB] Generation created:', data.id);
    return data.id;
    
  } catch (err) {
    console.error('[Supabase-JSONB] Failed to create generation:', err);
    
    // Last resort: Create with minimal data
    try {
      const fallbackId = crypto.randomUUID();
      const { error: fallbackError } = await (supabase as any)
        .from('character_generations')
        .insert({
          id: fallbackId,
          user_id: userId,
          status: 'PENDING',
          images: '[]', // Minimal JSONB array
        });
        
      if (!fallbackError) {
        console.log('[Supabase-JSONB] Created with fallback method:', fallbackId);
        return fallbackId;
      }
    } catch (fallbackErr) {
      console.error('[Supabase-JSONB] Fallback also failed:', fallbackErr);
    }
    
    throw err;
  }
};

// Helper function to update generation status
export const updateGenerationStatus = async (
  id: string,
  status: CharacterGeneration['status'],
  updates?: Partial<CharacterGeneration>
) => {
  // Convert images array to JSON string if present in updates
  const processedUpdates = updates ? {
    ...updates,
    images: updates.images ? JSON.stringify(updates.images) : undefined
  } : {};
  
  const { error } = await (supabase as any)
    .from('character_generations')
    .update({
      status,
      ...processedUpdates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);

  if (error) {
    console.error('[Supabase-JSONB] Update error:', error);
    throw error;
  }
};

// Helper to get generation with parsed images
export const getCharacterGeneration = async (id: string): Promise<CharacterGeneration | null> => {
  const { data, error } = await (supabase as any)
    .from('character_generations')
    .select('*')
    .eq('id', id)
    .single();
    
  if (error) {
    console.error('[Supabase-JSONB] Get error:', error);
    return null;
  }
  
  // Parse images from JSONB
  if (data && typeof data.images === 'string') {
    try {
      data.images = JSON.parse(data.images);
    } catch {
      data.images = [];
    }
  }
  
  return data;
};
